import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import moment from "moment";
import {
  Button,
  Dimmer,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
  SemanticICONS,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { Assign, OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { AccessibleDropdown } from "simplydo/react/components/Accessibility/AccessibleDropdown";
import { AccessibleModal } from "simplydo/react/components/Accessibility/AccessibleModal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaTags from "components/ideas/Tags";
import TermsCheckbox from "components/lib/Entry/TermsCheckbox";
import { FakeLink, TooltipButton } from "components/lib/UI";

const StyledButton = styled(Button)`
  min-width: max-content;
`;

type IdeaSubmissionNextStepProps = {
  to?: string;
  icon: string;
  text: string;
  onClick?: () => void;
};

const IdeaSubmissionNextStep = ({
  to,
  icon,
  text,
  onClick,
}: IdeaSubmissionNextStepProps) => {
  return (
    <div style={{ marginBottom: 25, fontSize: "larger", cursor: "pointer" }}>
      <Button as={to ? Link : undefined} to={to} secondary onClick={onClick}>
        <Icon style={{ paddingRight: 3 }} name={icon as SemanticICONS} />
        {text}
      </Button>
    </div>
  );
};

type SubmitIdeaButtonProps = {
  updateIdea: (data: any, fields: string[], save?: boolean) => void;
  onSubmit: (toSubmit: boolean) => void;
  isSaving: boolean;
  style: React.CSSProperties;
  size?: "tiny" | "small" | "medium" | "large" | "big" | "huge" | "massive";
  idea: Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaTemplates"],
    OpenAPI.Schemas["AssignIdeaChallenges"],
    OpenAPI.Schemas["AssignIdeaSubmissibilityStatus"]
  >;
  unsaved: boolean;
  isStatusBar: boolean;
};

const SubmitIdeaButton = ({
  updateIdea,
  onSubmit,
  isSaving,
  style,
  size = "tiny",
  idea,
  unsaved,
  isStatusBar,
}: SubmitIdeaButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState<false | number>(false);
  const [customTermsAgreed, setCustomTermsAgreed] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [anonymousSubmissionModalOpen, setAnonymousSubmissionModalOpen] =
    useState(false);
  const authToken = useMemo(() => {
    if (util.localStorageIsSupported()) {
      return localStorage.getItem(`ideaAuthToken:${idea._id}`);
    }
    return null;
  }, [idea._id]);

  const [nameIdeaModelOpen, setNameIdeaModelOpen] = useState(false);
  const [loadingAIIdeaName, setLoadingAIIdeaName] = useState(false);
  const [createAccountSubmitModalOpen, setCreateAccountSubmitModalOpen] =
    useState(false);
  const ideaId = idea?._id;
  const theme = useTheme();

  const canEditIdea = util.canEditIdea(user, idea);
  const canManageChallenge = util.canManageChallenge(user, idea.ownerChallenge);
  const canUpdateIdeaStatus = util.hasPermission(
    user,
    "challenge.updateIdeaStatus",
    idea.challenge,
  );
  const canEditIdeas = util.hasPermission(
    user,
    "challenge.editIdeas",
    idea.challenge,
  );
  const canManageChallenges = util.hasPermission(
    user,
    "org.manageChallenges",
    idea.ownerChallenge?.organisation,
  );

  const canSubmitWithRequiredFieldsMissing = useMemo(
    () => canManageChallenges || canEditIdeas,
    [canManageChallenges, canEditIdeas],
  );

  const defaultName = `${t("common:capitalise", { key: "generic.idea" })} for ${idea.ownerChallenge?.name}`;
  const isDefaultName = idea && idea.name === defaultName;

  const isSubmissible = idea?.submissibilityStatus?.isSubmissible;
  const publicIdeaCreationRequirement =
    idea?.ownerChallenge?.visibility?.publicIdeaCreationRequirement;
  const tagBeforeSubmission = idea?.ownerChallenge?.tagBeforeSubmission;
  const hasTags = (idea?.tags || []).length > 0;
  const hasTagList = (idea?.ownerChallenge?.tagList ?? []).length > 0;

  const challengeIsClosed = idea?.ownerChallenge?.stage === "closed";
  const submissionDeadline = idea?.submissionDeadline?.deadline;

  const tooltip = useMemo(() => {
    if (
      challengeIsClosed &&
      (!submissionDeadline ||
        (submissionDeadline && moment(submissionDeadline).isBefore()))
    ) {
      return `This ${t("generic.challenge")} is now closed to submissions.`;
    }
    return null;
  }, [challengeIsClosed, submissionDeadline, t]);

  const loadAIIdeaName = useCallback(() => {
    setLoadingAIIdeaName(true);
    api.ideas.createIdeaNameWithAI(
      ideaId,
      ({ success, name: aiName }) => {
        if (success) {
          updateIdea(aiName, ["name"]);
        }
        setLoadingAIIdeaName(false);
      },
      () => {
        setLoadingAIIdeaName(false);
      },
    );
  }, [ideaId, updateIdea]);

  const ideaName = idea?.name;
  const isSubmitted = idea?.isSubmitted;
  const ownerChallengeName = idea?.ownerChallenge?.name;
  const checkName = useCallback(
    (cb: (result: { nameChanged: boolean }) => void) => {
      if (
        !isSubmitted &&
        (!ideaName ||
          ideaName ===
            `${t("common:capitalise", { key: "generic.idea" })} for ${ownerChallengeName}`)
      ) {
        if (nameIdeaModelOpen === false) {
          loadAIIdeaName();
          setNameIdeaModelOpen(true);
        } else {
          toast.error("You must enter a name");
        }
      } else {
        setNameIdeaModelOpen(false);
        cb({ nameChanged: false });
      }
    },
    [
      isSubmitted,
      ideaName,
      ownerChallengeName,
      nameIdeaModelOpen,
      t,
      loadAIIdeaName,
    ],
  );

  const submitLogic = useCallback(
    (
      toSubmit: boolean,
      emailToRegister: string | null,
      bypassConfirmation = false,
    ) => {
      const doSubmit = () => {
        const upd: OpenAPI.PUT<"/ideas/{id}/status">["requestBody"] = {
          isSubmitted: toSubmit,
          authToken: authToken || undefined,
        };
        if (emailToRegister) {
          upd.registerUser = true;
          upd.email = emailToRegister;
          if (termsAgreed !== false) {
            upd.agreedTermsVersion = termsAgreed;
          }
        }
        setIsSubmitting(true);
        api.ideas.updateStatus(
          ideaId,
          upd,
          (newIdea) => {
            if (emailToRegister) {
              setAccountCreated(true);
              setEmailAddress("");
            }
            util.onUpdateIdea(newIdea, newIdea);

            if (newIdea.isSubmitted) {
              if (
                !user &&
                newIdea?.ownerChallenge?.visibility?.publicIdeaCreation &&
                newIdea?.ownerChallenge?.visibility
                  ?.publicIdeaCreationRequirement === "accountCreation"
              ) {
                setCreateAccountSubmitModalOpen(true);
              } else {
                setSubmitModalOpen(true);
              }
              onSubmit(toSubmit);
              setModalOpen(false);
              setAnonymousSubmissionModalOpen(false);
              setIsSubmitting(false);
            } else {
              toast.success(t("ideas.submit.confirm.unsubmitSuccess"));
              onSubmit(toSubmit);
              setModalOpen(false);
              setAnonymousSubmissionModalOpen(false);
              setIsSubmitting(false);
            }

            // Need to invalidate the ideas query fully to refetch project board ideas
            api.queryClient.invalidateQueries({
              queryKey: ["challenges", newIdea.challenge, "ideas"],
            });
          },
          (err) => {
            if (err.status === 400) {
              updateIdea("closed", ["ownerChallenge", "stage"], true);
            }
            toast.error(err.message);
            setIsSubmitting(false);
          },
        );
      };

      if (!isSubmissible && canSubmitWithRequiredFieldsMissing) {
        util
          .confirm(
            toSubmit ?
              t("ideas.submit.confirm.submit.skipRequiredTitle")
            : t("ideas.submit.confirm.withdraw.title"),
            toSubmit ?
              t("ideas.submit.confirm.submit.skipRequiredInfo")
            : t("ideas.submit.confirm.withdraw.info"),
          )
          .then(() => {
            doSubmit();
          })
          .catch(() => {});
      } else if (bypassConfirmation) {
        doSubmit();
      } else {
        util
          .confirm(
            toSubmit ?
              t("ideas.submit.confirm.submit.title")
            : t("ideas.submit.confirm.withdraw.title"),
            toSubmit ?
              t("ideas.submit.confirm.submit.info")
            : t("ideas.submit.confirm.withdraw.info"),
          )
          .then(() => {
            doSubmit();
          })
          .catch(() => {});
      }
    },
    [
      t,
      user,
      ideaId,
      onSubmit,
      termsAgreed,
      authToken,
      updateIdea,
      isSubmissible,
      canSubmitWithRequiredFieldsMissing,
    ],
  );

  const submitIdea = useCallback(
    (toSubmit: boolean, emailToRegister?: string) => {
      setModalOpen(false);
      checkName(({ nameChanged }) =>
        submitLogic(toSubmit, emailToRegister || null, nameChanged),
      );
    },
    [checkName, submitLogic],
  );

  const handleSubmission = useCallback(() => {
    if (!user && publicIdeaCreationRequirement !== "accountCreation") {
      setAnonymousSubmissionModalOpen(true);
    } else if (tagBeforeSubmission && !hasTags && hasTagList) {
      setModalOpen(true);
    } else {
      submitIdea(true);
    }
  }, [
    submitIdea,
    user,
    publicIdeaCreationRequirement,
    tagBeforeSubmission,
    hasTags,
    hasTagList,
  ]);

  const organisation = idea?.ownerChallenge?.organisation;
  const checkForAccount = useCallback(() => {
    setAccountLoading(false);
    setAccountCreated(false);
    api.auth.verifyAccount(
      organisation,
      emailAddress,
      null,
      ({ exists }) => {
        if (exists) {
          setAccountExists(true);
        } else {
          submitIdea(true, emailAddress);
        }
      },
      () => {
        setAccountLoading(false);
      },
    );
  }, [emailAddress, organisation, submitIdea]);

  const copyLink = useCallback(() => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard
        .writeText(
          `${util.getBaseUrl(user?.ownerOrganisation)}/ideas/${idea._id}`,
        )
        .then(
          () => toast.success("Link copied to your clipboard"),
          () => toast.error("Unable to copy the link"),
        );
    } else {
      toast.error("Your browser does not support copying");
    }
  }, [idea, user]);

  return (
    <>
      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        style={{ backgroundColor: "#fff" }}
      >
        <AccessibleModal.Header>
          {t("ideas.submit.naming.heading")}
        </AccessibleModal.Header>
        <AccessibleModal.Content>
          <p>{t("ideas.statusBar.tag.info")}</p>
          <IdeaTags idea={idea} updateIdea={updateIdea} isSubmission={true} />
        </AccessibleModal.Content>
        <AccessibleModal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => setModalOpen(false)}
          />
          <Button
            content={t("generic.submit")}
            primary
            icon="paper plane"
            onClick={() => submitIdea(true)}
          />
        </AccessibleModal.Actions>
      </AccessibleModal>

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={anonymousSubmissionModalOpen}
        onClose={() => setAnonymousSubmissionModalOpen(false)}
      >
        <AccessibleModal.Header>
          {t("ideas.submit.naming.heading")}
        </AccessibleModal.Header>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 20,
          }}
        >
          {(
            idea.ownerChallenge?.visibility?.publicIdeaCreationRequirement ===
            "requireEmail"
          ) ?
            <h3>Enter your email address to submit your {t("generic.idea")}</h3>
          : <h3>We recommend leaving your email address before you submit</h3>}
          <p>
            Whilst submitted the {t("generic.idea")} will no longer be editable.
            If you provide your email address below we&apos;ll set you up with a{" "}
            {util.appName()} account. This means you can come back later to stay
            updated about the
            {t("generic.challenge")} and to withdraw your submission should you
            wish to make amends.
          </p>
          {!accountCreated && !accountExists && (
            <Input
              placeholder={t("placeholder.email")}
              type="email"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                setAccountLoading(false);
              }}
              style={{ marginBottom: 20, width: 300 }}
            />
          )}
          {accountExists && (
            <>
              <Message info>
                <Message.Header>Account exists</Message.Header>
                <p>
                  An account already exists using this email. Click the button
                  below to login and assign this {t("generic.idea")} to your
                  account.
                </p>
              </Message>
              <Button
                content="Login"
                as={Link}
                secondary
                to={`/login?then=/ideas/${idea._id}`}
                style={{ width: 300, marginBottom: 20 }}
              />
            </>
          )}
          {emailAddress && !accountExists ?
            <>
              <TermsCheckbox
                organisation={user?.ownerOrganisation || urlOrganisation}
                agreed={termsAgreed === 2}
                onAgree={(checked) => setTermsAgreed(checked ? 2 : false)}
                agreedCustomTerms={customTermsAgreed}
                onAgreeCustomTerms={(checked) => setCustomTermsAgreed(checked)}
                style={{
                  marginBottom: 20,
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              />
              <Button
                disabled={!termsAgreed}
                content={`Create account & Submit ${t("generic.idea")}`}
                onClick={() => checkForAccount()}
                style={{ width: 300 }}
                loading={accountLoading}
                primary
              />
            </>
          : idea.ownerChallenge?.visibility?.publicIdeaCreationRequirement ===
              "noAction" && (
              <Button
                content={`Submit ${t("generic.idea")}`}
                onClick={() => submitIdea(true)}
                style={{ width: 300 }}
                primary
              />
            )
          }
          <span />

          <FakeLink
            onClick={() => {
              setAnonymousSubmissionModalOpen(false);
              setEmailAddress("");
              setAccountExists(false);
            }}
            style={{ marginTop: 10, color: "#000" }}
            withBorder
          >
            Continue editing
          </FakeLink>
        </div>
      </AccessibleModal>

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
      >
        <div
          style={{
            textAlign: "center",
            marginTop: 7,
            marginBottom: 10,
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          <div style={{ marginTop: 40, marginBottom: 20 }}>
            <Icon name="checkmark" size="huge" color="green" />
          </div>
          <Header as="h2" style={{ marginBottom: 20 }}>
            <small>
              Your {t("generic.idea")} was successfully submitted to{" "}
              {idea?.ownerChallenge?.name}
            </small>
          </Header>
          {(
            idea?.ownerChallenge &&
            (idea.ownerChallenge.ideaVisibility !== "users" ||
              !!idea.ownerChallenge?.ideaVisibilityLimits?.length)
          ) ?
            null
          : <>
              <Divider hidden />
              <Divider horizontal>
                <Header as="h4">{t("challenges.new.done.info")}</Header>
              </Divider>
              <AccessibleModal.Content>
                {accountCreated && (
                  <Message success style={{ marginLeft: 40, marginRight: 40 }}>
                    <h3>We&apos;ve set-up an account for you</h3>
                    <p>
                      Follow the link in the account setup email we sent you to
                      resume editing your {t("generic.idea")}.
                    </p>
                  </Message>
                )}
                <div
                  style={{
                    margin: 30,
                    display: theme.sizes.isMobile ? null : "flex",
                    justifyContent: "space-around",
                  }}
                >
                  {user?._id ?
                    <IdeaSubmissionNextStep
                      to={"/challenges/discover"}
                      icon="eye"
                      text={`Discover ${t("generic.challenges")}`}
                    />
                  : null}
                  {idea.ownerChallenge && (
                    <IdeaSubmissionNextStep
                      to={`/challenges/${idea.ownerChallenge?._id}`}
                      icon="target"
                      text={`View the ${t("generic.challenge")}`}
                    />
                  )}
                  <AccessibleDropdown
                    icon={null}
                    style={{ minWidth: "max-content" }}
                    pointing="top right"
                    trigger={
                      <IdeaSubmissionNextStep
                        icon="share"
                        text="Share your idea"
                      />
                    }
                    options={[
                      {
                        key: "share-x",
                        value: "share-x",
                        text: "Share on X",
                        icon: (
                          <div
                            style={{
                              display: "inline",
                              marginRight: 16,
                              marginLeft: 3,
                            }}
                          >
                            𝕏
                          </div>
                        ),
                        as: Link,
                        target: "_blank",
                        to: `https://x.com/intent/tweet?text=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea._id}`)}`,
                      },
                      {
                        key: "share-facebook",
                        value: "share-facebook",
                        text: "Share on Facebook",
                        icon: "facebook",
                        as: Link,
                        target: "_blank",
                        to: `https://www.facebook.com/sharer/sharer.php?u=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea._id}`)}`,
                      },
                      {
                        key: "copy-link",
                        value: "copy-link",
                        text: `Copy link to ${t("generic.idea")}`,
                        icon: "copy",
                        onClick: () => copyLink(),
                        onKeyDown: handleClickForKeyboard(() => copyLink()),
                      },
                    ]}
                  />
                </div>
              </AccessibleModal.Content>
            </>}
        </div>
        <AccessibleModal.Actions>
          <Button
            content={t("generic.close")}
            onClick={() => setSubmitModalOpen(false)}
          />
          {(
            idea?.ownerChallenge &&
            idea.ownerChallenge.ideaVisibility === "users"
          ) ?
            <Button
              primary
              content={`View ${t("generic.ideas")} created by others`}
              onClick={() => {
                setSubmitModalOpen(false);
                navigate(`/challenges/${idea.ownerChallenge._id}/ideas`);
              }}
            />
          : <Button
              content={`Return to my ${t("generic.idea")}`}
              onClick={() => setSubmitModalOpen(false)}
            />
          }
        </AccessibleModal.Actions>
      </AccessibleModal>

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={createAccountSubmitModalOpen}
        onClose={() => setCreateAccountSubmitModalOpen(false)}
      >
        <AccessibleModal.Header>
          Thanks for your submission
        </AccessibleModal.Header>
        <AccessibleModal.Content>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 20,
              paddingTop: 5,
            }}
          >
            <h3 style={{ marginBottom: 15 }}>
              Your {t("generic.idea")} was successfully submitted to{" "}
              {idea?.ownerChallenge?.name}.
            </h3>
            <p>
              Login or register for an account with {util.appName()} to track
              the progress of your {t("generic.idea")}. You will also then be
              able to engage with other {t("generic.challenges")} and view other
              available submitted {t("generic.ideas")} on {util.appName()}.
            </p>
            <Button
              content="Login or register"
              primary
              onClick={() => {
                setCreateAccountSubmitModalOpen(false);
                navigate(`/login?then=/challenges/${idea?.challenge}/ideas`);
              }}
            />
          </div>
        </AccessibleModal.Content>
      </AccessibleModal>

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={nameIdeaModelOpen}
      >
        <AccessibleModal.Header>
          {t("ideas.submit.naming.heading")}
        </AccessibleModal.Header>
        <AccessibleModal.Content>
          {loadingAIIdeaName ?
            <div style={{ padding: "20px 0" }}>
              <Dimmer active inverted>
                <Loader
                  active
                  content={`Generating ${t("generic.idea")} name...`}
                  inverted
                />
              </Dimmer>
            </div>
          : <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 20,
                paddingTop: 5,
              }}
            >
              <h3>{t("ideas.submit.naming.info")}</h3>
              <p>{t("ideas.submit.naming.help")}</p>
              <Input
                placeholder={t("ideas.submit.naming.placeholder")}
                value={isDefaultName ? "" : idea?.name}
                onChange={(e) =>
                  updateIdea(e.target.value || defaultName, ["name"])
                }
                style={{ minWidth: theme.sizes.isMobile ? "80vw" : 400 }}
              />
            </div>
          }
        </AccessibleModal.Content>
        <AccessibleModal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => setNameIdeaModelOpen(false)}
          />
          <TooltipButton
            disabled={!idea.name || isDefaultName || isSaving || unsaved}
            style={style}
            content={t("generic.submit")}
            icon={isSubmitting ? "spinner" : "paper plane"}
            onClick={handleSubmission}
            primary
            loading={isSubmitting}
            className="submit-idea-button"
            tooltip={
              !idea?.name || isDefaultName ?
                `You are required to give your ${t("generic.idea")} a name before submission`
              : undefined
            }
          />
        </AccessibleModal.Actions>
      </AccessibleModal>
      {canEditIdea || canManageChallenge || canUpdateIdeaStatus ?
        idea.isSubmitted ?
          <StyledButton
            style={style}
            size={size}
            content={
              canEditIdea ?
                theme.sizes.isMobile || canEditIdeas || canManageChallenges ?
                  `Unsubmit ${t("generic.idea")}`
                : t("ideas.editing")
              : t("generic.unsubmit")
            }
            icon={canEditIdeas || canManageChallenges ? "reply" : "edit"}
            onClick={() => submitIdea(false)}
            primary
            loading={isSubmitting}
            className="submit-idea-button"
          />
        : <TooltipButton
            disabled={
              isSaving ||
              unsaved ||
              (!idea?.ownerChallenge && !idea?.isSubmitted) ||
              (!isSubmissible && !canSubmitWithRequiredFieldsMissing) ||
              ((!idea.submissionDeadline ||
                (idea.submissionDeadline &&
                  moment(idea.submissionDeadline?.deadline).isBefore())) &&
                idea?.ownerChallenge?.stage === "closed")
            }
            style={style}
            size={size}
            fluid
            content={t("generic.submit")}
            icon={isSubmitting ? "spinner" : "paper plane"}
            onClick={handleSubmission}
            primary
            loading={isSubmitting}
            className="submit-idea-button"
            tooltip={tooltip}
            tooltipProps={{
              position: isStatusBar ? "bottom right" : "top center",
              style: { maxWidth: 400 },
            }}
          />

      : null}
    </>
  );
};

export default SubmitIdeaButton;
